import { useMemo } from "react";
import type { RouteComponentProps } from "@reach/router";

import { useCompanyPreviewData } from "../hooks/useCompanyPreviewData";
import { DocumentCategoryGroup } from "./DocumentCategoryGroup";
import type { CorporateDocument } from "newui/features/Company/types";

type CorporateDocumentsTabProps = RouteComponentProps;

interface DocumentsByCategory {
	categoryId: number;
	categoryName: string;
	documents: CorporateDocument[];
}

export const CorporateDocumentsTab: React.FC<
	CorporateDocumentsTabProps
> = () => {
	const { documents } = useCompanyPreviewData();

	const grouped = useMemo((): DocumentsByCategory[] => {
		const categoryMap = new Map<number, DocumentsByCategory>();

		for (const doc of documents) {
			if (!doc.category) continue;

			const catId = doc.category.parent_id || doc.category.id;
			const catName = doc.category.parent_id
				? doc.category.name
				: doc.category.name;

			if (!categoryMap.has(catId)) {
				categoryMap.set(catId, {
					categoryId: catId,
					categoryName: catName,
					documents: [],
				});
			}

			categoryMap.get(catId)!.documents.push(doc);
		}

		return Array.from(categoryMap.values());
	}, [documents]);

	if (documents.length === 0) {
		return (
			<p className="py-8 text-center text-sm text-gray-500">
				No corporate documents available.
			</p>
		);
	}

	return (
		<div className="space-y-6">
			{grouped.map((group) => (
				<DocumentCategoryGroup
					key={group.categoryId}
					title={group.categoryName}
					documents={group.documents}
				/>
			))}
		</div>
	);
};
